import { useState } from "react";
import { CalendarClock, Loader2, CalendarIcon } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Calendar } from "@/components/ui/calendar";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { format, addDays } from "date-fns";
import { cn } from "@/lib/utils";

interface ReschedulePackageDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  packageId: string | null;
  onSuccess: () => void;
}

const TIME_SLOTS = [
  "8:00 AM - 10:00 AM",
  "10:00 AM - 12:00 PM",
  "12:00 PM - 2:00 PM",
  "2:00 PM - 4:00 PM",
  "4:00 PM - 6:00 PM",
];

export const ReschedulePackageDialog = ({
  open,
  onOpenChange,
  packageId,
  onSuccess,
}: ReschedulePackageDialogProps) => {
  const [saving, setSaving] = useState(false);
  const [selectedDate, setSelectedDate] = useState<Date | undefined>(undefined);
  const [selectedTimeSlot, setSelectedTimeSlot] = useState<string>("");

  const handleReschedule = async () => {
    if (!packageId || !selectedDate || !selectedTimeSlot) {
      toast.error("Please select both date and time slot");
      return;
    }

    setSaving(true);
    try {
      // Get the pickup schedule for this package
      const { data: scheduleData, error: fetchError } = await supabase
        .from("pickup_schedules")
        .select("id")
        .eq("package_id", packageId)
        .maybeSingle();

      if (fetchError) throw fetchError;

      const formattedDate = format(selectedDate, "yyyy-MM-dd");

      if (scheduleData) {
        // Update existing schedule
        const { error: updateError } = await supabase
          .from("pickup_schedules")
          .update({
            pickup_date: formattedDate,
            pickup_time_slot: selectedTimeSlot,
          })
          .eq("id", scheduleData.id);

        if (updateError) throw updateError;
      } else {
        // Get package info to create new schedule
        const { data: pkgData, error: pkgError } = await supabase
          .from("packages")
          .select("user_id, profiles!packages_user_id_fkey(street, city, state, zip)")
          .eq("id", packageId)
          .single();

        if (pkgError) throw pkgError;

        const profile = pkgData?.profiles as any;
        const address = profile
          ? `${profile.street || ""}, ${profile.city || ""}, ${profile.state || ""} ${profile.zip || ""}`.trim()
          : "Address not available";

        // Create new schedule
        const { error: insertError } = await supabase
          .from("pickup_schedules")
          .insert({
            package_id: packageId,
            user_id: pkgData?.user_id,
            pickup_date: formattedDate,
            pickup_time_slot: selectedTimeSlot,
            address: address,
          });

        if (insertError) throw insertError;
      }

      // Update package status back to scheduled
      await supabase
        .from("packages")
        .update({ status: "scheduled" })
        .eq("id", packageId);

      toast.success("Package rescheduled successfully");
      onSuccess();
      handleClose();
    } catch (error) {
      if (import.meta.env.DEV) {
        console.error("Error rescheduling package:", error);
      }
      toast.error("Failed to reschedule package");
    } finally {
      setSaving(false);
    }
  };

  const handleClose = () => {
    setSelectedDate(undefined);
    setSelectedTimeSlot("");
    onOpenChange(false);
  };

  // Minimum date is tomorrow
  const minDate = addDays(new Date(), 1);

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <CalendarClock className="h-5 w-5" />
            Reschedule Package Pickup
          </DialogTitle>
          <DialogDescription>
            The original pickup date has passed. Select a new date and time for this package.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Date Picker */}
          <div className="space-y-2">
            <label className="text-sm font-medium">New Pickup Date</label>
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  className={cn(
                    "w-full justify-start text-left font-normal",
                    !selectedDate && "text-muted-foreground"
                  )}
                >
                  <CalendarIcon className="mr-2 h-4 w-4" />
                  {selectedDate ? format(selectedDate, "PPP") : "Select date"}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="start">
                <Calendar
                  mode="single"
                  selected={selectedDate}
                  onSelect={setSelectedDate}
                  disabled={(date) => date < minDate}
                  initialFocus
                  className={cn("p-3 pointer-events-auto")}
                />
              </PopoverContent>
            </Popover>
          </div>

          {/* Time Slot */}
          <div className="space-y-2">
            <label className="text-sm font-medium">Time Slot</label>
            <Select value={selectedTimeSlot} onValueChange={setSelectedTimeSlot}>
              <SelectTrigger className="w-full">
                <SelectValue placeholder="Select time slot" />
              </SelectTrigger>
              <SelectContent>
                {TIME_SLOTS.map((slot) => (
                  <SelectItem key={slot} value={slot}>
                    {slot}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        <DialogFooter className="gap-2 sm:gap-0">
          <Button variant="outline" onClick={handleClose} disabled={saving}>
            Cancel
          </Button>
          <Button
            onClick={handleReschedule}
            disabled={!selectedDate || !selectedTimeSlot || saving}
          >
            {saving ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Rescheduling...
              </>
            ) : (
              <>
                <CalendarClock className="h-4 w-4 mr-2" />
                Reschedule Pickup
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
